import { useTenantMemberships } from "@/contexts/TenantContext";
import { LazoThreadConversationStatus } from "@/enums/LazoThreadConversationStatus";
import useWebSocketSubscription from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/hooks/useWebSocketSubscription";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import NotificationService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/NotificationService";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoChatConversation, LazoChatConversationShell, mapToShell } from "@/models/ai-chat/LazoChatConversation";
import { LazoChatMessage } from "@/models/ai-chat/LazoChatMessage";
import { LazoChatTypingDTO } from "@/models/ai-chat/LazoChatTyping";
import { SmallConversationMember, mapToSmallConversationMembers } from "@/models/ai-chat/SmallConversationMember";
import LazoChatConversationService from "@/services/ai-chat/LazoChatConversationService";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { ConversationType } from "@/types/ConversationType";
import ChatUserWebSocketClient from "@/utils/websocket/ChatUserWebSocketClient";
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { EmptyChatsScreen } from "../empty-chat-components/EmptyChatsScreen";
import { LazoPortalNewChatButtonEvents } from "./LazoPortalNewChatButton";
import { mapToNewListWithUpdatedTarget, usePortalChatContext } from "./PortalChatContext";
import { PortalChatList } from "./PortalChatList";
import PortalChatView from "./PortalChatView";

export const ChatParams = {
  LazoChatTeamExternalId: ":LazoChatTeamExternalId",
};

export enum CHANNELS {
  REPLY,
  TEAM,
}

export function PortalChatContainer() {
  const {
    chats,
    setChats,
    privateChat,
    setPrivateChat,
    groupChat,
    setGroupChat,
    step,
    setStep,
    selectedChat,
    setSelectedChat,
    members,
    setMembers,
    messages,
    setMessages,
    loadingPage,
    firstLoad,
    filters,
    setFilters,
    generalChat,
    setGeneralChat,
    handleChatSelect,
    embedded,
  } = usePortalChatContext();

  const { isMobile, isTablet } = useResponsive();

  // Mobile view state: true = show chat view, false = show chat list
  const [showMobileChatView, setShowMobileChatView] = useState<boolean>(false);

  useEffect(() => {
    chats.length !== 0 && setShowEmptyChatScreen(false);
  }, [chats.length]);

  const [userTyping, setUserTyping] = useState<LazoChatTypingDTO | null>();
  const [typingTimeout, setTypingTimeout] = useState<any>(null);
  const currentUserExternalId: string = AuthService.getUser()?.externalId ?? "";

  const [newConversationMembers, setNewConversationMembers] = useState<string[]>([]);
  const [showEmptyChatScreen, setShowEmptyChatScreen] = useState<boolean>(true);

  // Handle mobile chat selection - show chat view on mobile
  const handleMobileChatSelect = (chat: LazoChatConversationShell) => {
    handleChatSelect(chat);
    if (isMobile) {
      setShowMobileChatView(true);
    }
  };

  // Handle back to chat list on mobile
  const handleBackToList = () => {
    if (isMobile) {
      setShowMobileChatView(false);
    }
  };

  // Reset mobile view when switching away from mobile
  useEffect(() => {
    if (!isMobile) {
      setShowMobileChatView(false);
    }
  }, [isMobile]);
  const { currentTenantMembership } = useTenantMemberships();

  useWebSocketSubscription(ChatUserWebSocketClient, AuthService.getUser()?.externalId!, (s) => handleWsMessage(s));

  // START WEBSOCKET HANDLERS
  const handleWsMessage = (jsonMessage: string) => {
    const message = JSON.parse(jsonMessage);
    if (selectedChat.readOnly) {
      return;
    }
    switch (message.type) {
      case "NEW_MESSAGE":
        if (selectedChat?.externalId === message.content.conversationExternalId) {
          receiveMessage(message.content);
        } else {
          setChats((prev) =>
            prev.map((c) => {
              if (c.externalId === message.content.conversationExternalId) {
                return {
                  ...c,
                  currentThreadStatus: message.content.associatedThreadStepStatus,
                  unreadMessagesCount: c.unreadMessagesCount ? c.unreadMessagesCount + 1 : 1,
                  lastMessageText: message.content.message,
                };
              } else if (c.internalChannelExternalId === message.content.conversationExternalId) {
                return {
                  ...c,
                  internalUnreadMessageCount: c.internalUnreadMessageCount ? c.internalUnreadMessageCount + 1 : 1,
                  lastMessageText: message.content.message,
                };
              }
              return c;
            })
          );

          if (generalChat?.internalChannelExternalId === message.content.conversationExternalId) {
            setGeneralChat({
              ...generalChat,
              internalUnreadMessageCount: generalChat.internalUnreadMessageCount ? generalChat.internalUnreadMessageCount + 1 : 1,
            });
          }
        }
        break;
      case "USER_TYPING":
        setUserTyping(() => {
          return message.content;
        });

        if (typingTimeout) {
          clearTimeout(typingTimeout);
        }

        const newTypingTimeout = setTimeout(() => {
          setUserTyping(null);
        }, message.content.timeToLive - Date.now());
        setTypingTimeout(newTypingTimeout);

        break;
    }
  };

  function startNewChat(message: string) {
    function addToChats(c: LazoChatConversationShell) {
      setChats((prev) => {
        if (prev.find((current) => current.externalId === c.externalId)) {
          return prev;
        } else {
          return prev.concat(c);
        }
      });
    }

    if (newConversationMembers.length > 1) {
      LazoChatConversationService.createPrivateGroupChat(
        newConversationMembers,
        null,
        currentTenantMembership.tenant.externalId,
        message
      ).then((r) => {
        r.onSuccess((c) => {
          const shell = mapToShell(c);
          addToChats(shell);
          handleChatSelect(shell);
          setStep(0);
        });
        r.onError((m) => {
          NotificationService.error("There was an error creating the conversation. Error: " + m);
        });
      });
    } else if (newConversationMembers.length === 1) {
      LazoChatConversationService.createPrivateChat(newConversationMembers[0], currentTenantMembership.tenant.externalId, message).then(
        (r) => {
          r.onSuccess((c) => {
            const shell = mapToShell(c);
            addToChats(shell);
            handleChatSelect(shell);
            setStep(0);
          });
          r.onError((e) => {
            NotificationService.error("There was an error creating the conversation. Error: " + e);
          });
        }
      );
    }
  }

  const sendMessage = (message: string, conversation: LazoChatConversationShell, mentions?: { id: string }[], files?: File[]) => {
    if (conversation?.readOnly) {
      NotificationService.error("You are not part of this conversation.");
      return;
    }
    if (!conversation) {
      startNewChat(message);
      return;
    }

    if (mentions) {
      addTaggedMembers(mentions, members, conversation);
    }

    const createMessage = () => {
      return LazoChatMessageService.createNewMessage(message, conversation.externalId, false);
    };

    const sendAttachments = () => {
      return LazoChatMessageService.sendAttachments(conversation.externalId, files);
    };

    // Mentions are only available on chats with people
    if (mentions && mentions.length > 0) {
      handleMentions(mentions, conversation);
    }

    const promises = [];
    if (message) {
      promises.push(createMessage());
    }
    if (files && files.length !== 0) {
      promises.push(sendAttachments());
    }

    Promise.all(promises).then((responses) => {
      const existingMessages = Array.isArray(messages) ? messages : [];

      const newMessages: LazoChatMessage[] = [...existingMessages, ...responses.map((response) => response.getContentOrThrowError()!)];

      setMessages(newMessages);

      const threadUpdate: LazoThreadConversationStatus = newMessages[newMessages.length - 1]?.associatedThreadStepStatus;
      if (threadUpdate) {
        setSelectedChat((prev) => ({ ...prev, currentThreadStatus: threadUpdate }));
      }
    });
  };

  const receiveMessage = (newMessage: LazoChatMessage) => {
    const messageExists = messages.some((msg) => msg.externalId === newMessage.externalId);
    if (messageExists) {
      return;
    }

    setMessages((prev) => prev.concat(newMessage));
    const threadStatusUpdate = newMessage.associatedThreadStepStatus;

    const partialUpdate: Partial<LazoChatConversationShell> = {
      lastMessageText: newMessage.message,
      currentThreadStatus: threadStatusUpdate || selectedChat.currentThreadStatus,
    };

    const newSelectedChat: LazoChatConversationShell = { ...selectedChat, ...partialUpdate };

    if (threadStatusUpdate && selectedChat.currentThreadStatus !== threadStatusUpdate) {
      setChats(mapToNewListWithUpdatedTarget(chats, selectedChat.externalId, partialUpdate));
    }

    handleChatSelect(newSelectedChat);
  };

  // END WEBSOCKET HANDLERS
  function handleMentions(mentions: { id: string }[], currentChat: LazoChatConversationShell) {
    LazoChatConversationService.addMentionsToConversationMembers(mentions, currentChat.externalId)
      // Ignore result, be optimistic
      .then();
  }

  // This has to be used only when we want to add people, not every time we send a message with a tag
  const addTaggedMembers = (mentions: { id: string }[], currentMembers: SmallConversationMember[], chat: LazoChatConversationShell) => {
    if (!mentions || mentions.length === 0) return;
    //check if user is not in conversation
    if (
      currentMembers.some(
        (member) => member.userExternalId === mentions[0].id && chat.conversationType === ConversationType.INTERNAL_CHANNEL
      )
    ) {
      return;
    }
    const userExternalId: string[] = mentions.map((mention) => mention.id);
    LazoChatConversationService.addTaggedMembers(userExternalId, chat.externalId).then((response) => {
      response.onSuccess((c) => {
        setMembers(c.members.map((cm) => mapToSmallConversationMembers(cm)));
      });

      response.onError((m) => {
        NotificationService.error(m);
      });
    });
  };

  const readMessages = (externalId: string) => {
    if (!externalId) return;

    LazoChatConversationService.readConversation(externalId).then((_) => {
      setChats((prev) =>
        prev.map((c) => {
          if (c.externalId === externalId) {
            return { ...c, unreadMessagesCount: 0 };
          } else if (c.internalChannelExternalId === externalId) {
            return { ...c, internalUnreadMessageCount: 0 };
          }
          return c;
        })
      );
    });
  };

  const location = useLocation();

  useEffect(() => {
    if (location.state) {
      const { conversation }: { conversation: LazoChatConversation } = location.state;
      setSelectedChat(mapToShell(conversation));
    }
  }, []);

  useEffect(() => {
    if (!chats?.length) {
      return;
    }

    if (!selectedChat) {
      const newSelectedChat = chats.find((c) => c.createdAt! >= Date.now().toString()) || null;
      handleChatSelect(newSelectedChat);
    }
  }, [chats]);

  return (
    <MainContainer>
      {/* Mobile Layout: Show either chat list or chat view */}
      {isMobile ? (
        <>
          {!showMobileChatView ? (
            // Show chat list on mobile
            <ListChatContainer
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              <PortalChatList
                chats={chats}
                setChats={setChats}
                onChatSelect={handleMobileChatSelect}
                userTyping={userTyping}
                selectedChat={selectedChat}
                privateChat={privateChat}
                setPrivateChat={setPrivateChat}
                groupChat={groupChat}
                setGroupChat={setGroupChat}
                step={step}
                setStep={setStep}
                loadingPage={loadingPage}
                firstLoad={firstLoad}
                filters={filters}
                setFilters={setFilters}
              />
            </ListChatContainer>
          ) : (
            // Show chat view on mobile
            <ChatViewContainer
              sx={{
                width: "100%",
                height: "100%",
              }}
            >
              {chats.length !== 0 || !showEmptyChatScreen ? (
                <PortalChatView
                  onChatSelect={handleMobileChatSelect}
                  sendMessage={sendMessage}
                  userTyping={userTyping}
                  currentUserExternalId={currentUserExternalId}
                  setNewConversationMembers={setNewConversationMembers}
                  onBackToList={handleBackToList}
                  showBackButton={true}
                />
              ) : (
                <EmptyChatsScreen
                  readOnly={embedded}
                  handleStartChatFromEmpty={(ev) =>
                    document.dispatchEvent(new CustomEvent(LazoPortalNewChatButtonEvents.OpenMenu, { detail: { target: ev.target } }))
                  }
                />
              )}
            </ChatViewContainer>
          )}
        </>
      ) : (
        // Desktop Layout: Show both side by side
        <>
          <ListChatContainer
            sx={{
              flex: { xs: 1, md: "none" },
              width: { xs: "100%", md: "33%" },
            }}
          >
            <PortalChatList
              chats={chats}
              setChats={setChats}
              onChatSelect={handleChatSelect}
              userTyping={userTyping}
              selectedChat={selectedChat}
              privateChat={privateChat}
              setPrivateChat={setPrivateChat}
              groupChat={groupChat}
              setGroupChat={setGroupChat}
              step={step}
              setStep={setStep}
              loadingPage={loadingPage}
              firstLoad={firstLoad}
              filters={filters}
              setFilters={setFilters}
            />
          </ListChatContainer>
          {chats.length !== 0 || !showEmptyChatScreen ? (
            <ChatViewContainer
              sx={{
                flex: { xs: 0, md: 2 },
                width: { xs: "0%", md: "67%" },
              }}
            >
              <PortalChatView
                onChatSelect={handleChatSelect}
                sendMessage={sendMessage}
                userTyping={userTyping}
                currentUserExternalId={currentUserExternalId}
                setNewConversationMembers={setNewConversationMembers}
                onBackToList={handleBackToList}
                showBackButton={false}
              />
            </ChatViewContainer>
          ) : (
            <EmptyChatsScreen
              readOnly={embedded}
              handleStartChatFromEmpty={(ev) =>
                document.dispatchEvent(new CustomEvent(LazoPortalNewChatButtonEvents.OpenMenu, { detail: { target: ev.target } }))
              }
            />
          )}
        </>
      )}
    </MainContainer>
  );
}

const ListChatContainer = styled(Box)(({ theme }) => ({
  zIndex: theme.zIndex.appBar,
  boxShadow: "0 0 1.306rem rgba(0, 0, 0, 0.1)",
  backgroundColor: "#fff",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
}));

const ChatViewContainer = styled(Box)(() => ({}));

const MainContainer = styled(Box)(() => ({
  display: "flex",
  width: "100%",
  height: "100%",
  overflowY: "hidden",
}));

export default PortalChatContainer;
